import * as React from "react";
import queryString from "query-string";
import UniversalRouter, { RouteError, ResolveContext, RouteContext, RouterOptions } from "universal-router";
import { UnauthorizedComponent } from "./components/errors/UnauthorizedComponent";
import OopsComponent from "./components/errors/OopsComponent";
import { LoginComponent } from "./components/LoginComponent";
import { ForgotPasswordComponent } from './components/ForgotPasswordComponent'
import { ResetPasswordComponent } from './components/ResetPasswordComponent'
import { CognitoContainer } from "./CognitoContainer";
import { IRoute } from "./types/IRoute";
import User from "../models/User";
import { Sendum } from "../Sendum";
import { TPage } from "./types/TPage";
import { Logger } from "../utils/Logger";

const logger = new Logger("CognitoContainerRouter");

export class CognitoContainerRouter {
	private static instance: CognitoContainerRouter;

	private static readonly defaultRootRoute = {
		path: "/",
		isDefault: true,
		render: () => (
			<CognitoContainer>
				<div>Default Index</div>
			</CognitoContainer>
		),
	};

	private static readonly defaultLoginRoute = {
		path: "/login",
		render: () => (
			<CognitoContainer>
				<LoginComponent />
			</CognitoContainer>
		),
	};

	private static readonly defaultForgotPasswordRoute = {
		path: "/forgot-password",
		render: () => (
			<CognitoContainer>
				<ForgotPasswordComponent/>
			</CognitoContainer>
		),
	}

	private static readonly defaultResetPasswordRoute = {
		path: '/reset-password/:code',
		render: (context: RouteContext): JSX.Element => (
			<CognitoContainer>
				<ResetPasswordComponent
					code={context.params.code as string} />
			</CognitoContainer>
		),
	}

	private static readonly defaultRoutes: Map<string, IRoute> = new Map([
		["root", CognitoContainerRouter.defaultRootRoute],
		["login", CognitoContainerRouter.defaultLoginRoute],
		["forgot-password", CognitoContainerRouter.defaultForgotPasswordRoute],
		["reset-password", CognitoContainerRouter.defaultResetPasswordRoute],
	]);

	private static readonly resolveRouteCognito = (context: RouteContext, params: any, isAuth: boolean): TPage => {
		logger.debug("resolveRouteCognito");
		if (!isAuth) {
			return { redirect: CognitoContainerRouter.getDefaultPublicPath() };
		}
		Sendum.initApiGatewayClient();

		const route = context.route as IRoute;
		if (typeof route.render === "function") {
			return { content: route.render(context, params) };
		}

		if (route.redirectPath) {
			return { redirect: route.redirectPath };
		}

		return undefined;
	};

	private static readonly resolveRouteAuthKey = (context: RouteContext, params: any, apiKey?: string): TPage => {
		Sendum.initAuthKeyClient(apiKey);

		const route = context.route as IRoute;
		if (typeof route.render === "function") {
			return { content: route.render(context, params) };
		}

		return undefined;
	};

	private static readonly resolveRouteAuthToken = (context: RouteContext<TPage>, params: any): TPage => {
		Sendum.initAuthTokenClient(queryString.parse(context.search as string).token as string);

		const route = context.route as IRoute;
		if (typeof route.render === "function") {
			return { content: route.render(context, params) };
		}

		return undefined;
	};

	private static readonly options: RouterOptions<TPage> = {
		resolveRoute(context: RouteContext<TPage>, params: any) {
			if (!context.route?.path) {
				return undefined;
			}

			if (context.route?.path === '/' && context.hash.startsWith('#/')) {
				return { redirect: CognitoContainerRouter.getPathWithoutHash(context.hash)};
			}

			const isAuth = new User().isAuthorized();
			const route = context.route as IRoute;
			switch (route.private) {
				case "cognito": {
					return CognitoContainerRouter.resolveRouteCognito(context, params, isAuth);
				}
				case "authKey": {
					return CognitoContainerRouter.resolveRouteAuthKey(context, params);
				}
				case "authKeyShared": {
					return CognitoContainerRouter.resolveRouteAuthKey(context, params, "REACT_APP_SENDUM_SHARED_AUTH_KEY");
				}
				case "authToken": {
					return CognitoContainerRouter.resolveRouteAuthToken(context, params);
				}
				default: {
					if (isAuth) {
						return { redirect: CognitoContainerRouter.getDefaultPrivatePath() };
					}

					if (typeof route.render === "function") {
						return { content: route.render(context, params) };
					}

					return undefined;
				}
			}
		},
		errorHandler(error: RouteError, context: ResolveContext) {
			logger.error("CognitoContainerRouter >> Error ", error, context);

			switch (error.status) {
				case 401: {
					return { content: <UnauthorizedComponent /> };
				}
				case 404: {
					if (new User().isAuthorized()) {
						return { redirect: CognitoContainerRouter.getDefaultPrivatePath() };
					}

					return { redirect: CognitoContainerRouter.getDefaultPublicPath() };

				}
				default: {
					return { content: OopsComponent };
				}
			}
		},
	};

	private router: UniversalRouter<TPage>;

	private constructor() {
	}

	private static getInstance(): CognitoContainerRouter {
		if (!CognitoContainerRouter.instance) {
			CognitoContainerRouter.instance = new CognitoContainerRouter();
		}

		return CognitoContainerRouter.instance;
	}

	public static getRouter(customRoutes: Map<string, IRoute>): UniversalRouter<TPage> {
		const cognitoContainerRouter: CognitoContainerRouter = CognitoContainerRouter.getInstance();

		if (!cognitoContainerRouter.router) {
			const mergedRoutes: Map<string, IRoute> = new Map([...CognitoContainerRouter.defaultRoutes, ...customRoutes]);
			const routes: IRoute[] = Array.from(mergedRoutes.values());
			cognitoContainerRouter.router = new UniversalRouter<TPage>(routes, CognitoContainerRouter.options);
		}

		return cognitoContainerRouter.router;
	}

	private static readonly getDefaultPublicPath = (): string =>
		// TODO
		"/login";


	private static readonly getDefaultPrivatePath = (): string =>
		// TODO
		"/visibility";

	private static readonly getPathWithoutHash = (hash:string): string => {
		return hash.slice(1);
	}

}
